/** Student form helpers: DTO builders, hydration, validation. */
import {
  normalizeStudentAccountStatus,
  type ImportCsvResult,
  type CreateStudentDto,
  type CreateStudentFormState,
  type EditStudentFormState,
  type StudentAccountStatus,
  type UpdateStudentDto,
} from '@/types/inperson/student.types';
import { normalizeProfileGender, type WeeklyAvailabilitySlot } from '@/types/user.types';
import { toCalendarDateString, toLocalDateString } from '@/utils/format-date-utils';
import type { SelectOption } from '@/components/forms/Select';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import {
  ESTABLISHMENT_TYPE_TO_CYCLE,
  EXIT_REASON_VALUES,
  KNOWN_SPECIAL_NEEDS_TYPES,
} from '@/components/students/student-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { getDefaultCompanyCountryIso } from '@/utils/user-contact-utils';
import { ALLOWED_INPERSON_TUTOR_DEPARTMENT_CODES } from '@/components/tutors/tutor-configs';
export {
  getStudentEditStatusSelectOptions,
  resolvePreviousStatusBeforeDeactivate,
} from '@/utils/common-utils';

/** Partial student GET/list row used to hydrate the in-person form. */
type StudentRecord = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  status?: string;
  deactivationReason?: string | null;
  deactivationReasonNote?: string | null;
  deactivationDate?: string | Date | null;
  programIds?: unknown;
  coursePath?: string | null;
  userEstablishments?: Array<{
    establishmentId?: string;
    establishment?: { id?: string; name?: string } | null;
  }>;
  userProfiles?: Array<{
    dateOfBirth?: string | null;
    gender?: string | null;
    levelId?: string | null;
    schoolClassId?: string | null;
    addressLine1?: string | null;
    department?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
    subjects?: unknown[] | null;
    availability?: unknown;
    hasSpecialNeeds?: boolean;
    specialNeedsType?: string | null;
    specialNeedsNote?: string | null;
    isDisabled?: boolean;
    disabilityTypes?: string[] | null;
    disabilityOtherNote?: string | null;
    medicalNotes?: string | null;
    exitDate?: string | Date | null;
    schoolClass?: { establishment?: { id: string; name: string } } | null;
    userProfileSpecialties?: Array<{ specialty: { id: string } }> | null;
  }>;
};

/** Toast payload after a CSV import. */
export type StudentImportToast = {
  kind: 'success' | 'info' | 'warning' | 'error';
  message: string;
};

/** Establishment fields used to build a cached select label. */
export type EstablishmentLabelEntry = {
  id: string;
  name: string;
  city?: string | null;
  postalCode?: string | null;
  department?: string | null;
  country?: string | null;
};

/** Empty edit form (hydrated later). */
export function emptyEditStudentForm(): EditStudentFormState {
  return {
    firstName: '',
    lastName: '',
    displayName: '',
    status: 'registered',
    dateOfBirth: '',
    gender: '',
    levelId: '',
    schoolClassId: '',
    addressLine1: '',
    department: '',
    postalCode: '',
    city: '',
    country: getDefaultCompanyCountryIso(),
    establishmentIds: [],
    subjects: [],
    availability: { weekly: [] },
    hasSpecialNeeds: false,
    specialNeedsType: '',
    specialNeedsNote: '',
    isDisabled: false,
    disabilityTypes: [],
    disabilityOtherNote: '',
    medicalNotes: '',
    specialtyIds: [],
    deactivationReasonType: '',
    deactivationReasonOther: '',
    exitDate: '',
    programIds: [],
    coursePath: '',
  };
}

/** Empty create form; country falls back to the company ISO code. */
export function emptyCreateStudentForm(): CreateStudentFormState {
  return {
    firstName: '',
    lastName: '',
    displayName: '',
    dateOfBirth: '',
    gender: '',
    levelId: '',
    schoolClassId: '',
    addressLine1: '',
    department: '',
    postalCode: '',
    city: '',
    country: getDefaultCompanyCountryIso(),
    roleIds: [],
    establishmentIds: [],
    subjects: [],
    availability: { weekly: [] },
    hasSpecialNeeds: false,
    specialNeedsType: '',
    specialNeedsNote: '',
    isDisabled: false,
    disabilityTypes: [],
    disabilityOtherNote: '',
    medicalNotes: '',
    status: 'registered',
    specialtyIds: [],
    programIds: [],
    coursePath: '',
  };
}

/** Changing establishment invalidates level / class / specialties. */
function applyEstablishmentCascade<
  T extends { levelId: string; schoolClassId: string; specialtyIds: string[] },
>(next: T): T {
  return { ...next, levelId: '', schoolClassId: '', specialtyIds: [] };
}

/** Changing level invalidates class / specialties. */
function applyLevelCascade<T extends { schoolClassId: string; specialtyIds: string[] }>(
  next: T,
): T {
  return { ...next, schoolClassId: '', specialtyIds: [] };
}

/** Patch one create-form field; cascades establishment/level clears. */
export function applyCreateStudentField(
  form: CreateStudentFormState,
  field: keyof CreateStudentFormState,
  value: unknown,
): CreateStudentFormState {
  let next = { ...form, [field]: value } as CreateStudentFormState;
  if (field === 'establishmentIds') next = applyEstablishmentCascade(next);
  if (field === 'levelId') next = applyLevelCascade(next);
  return next;
}

/** Patch one edit-form field; cascades establishment/level/deactivation clears. */
export function applyEditStudentField(
  form: EditStudentFormState,
  field: string,
  value: unknown,
): EditStudentFormState {
  let next = { ...form, [field]: value } as EditStudentFormState;
  if (field === 'establishmentIds') next = applyEstablishmentCascade(next);
  if (field === 'levelId') next = applyLevelCascade(next);
  if (field === 'status' && value !== 'deactivated') {
    next = { ...next, deactivationReasonType: '', deactivationReasonOther: '', exitDate: '' };
  }
  if (field === 'status' && value === 'deactivated' && !next.exitDate) {
    next = { ...next, exitDate: toLocalDateString(new Date()) };
  }
  if (field === 'deactivationReasonType' && value !== 'other') {
    next = { ...next, deactivationReasonOther: '' };
  }
  return next;
}

/** Fill subjects from catalog defaults only when the form has none. */
export function applyDefaultSubjectsIfEmpty(
  form: CreateStudentFormState,
  defaultIds: string[],
): CreateStudentFormState {
  if (form.subjects.length > 0 || defaultIds.length === 0) return form;
  return { ...form, subjects: defaultIds };
}

/** Suggested display name from first + last (privacy-safe last name is already truncated). */
export function generateStudentDisplayName(firstName: string, lastName: string): string {
  const first = firstName.trim();
  const last = lastName.trim();
  if (!first && !last) return '';
  if (first && last) return `${first} ${last}`;
  return first || last;
}

/** Uniqueness check is only useful once last name has 2+ characters. */
export function shouldCheckDisplayName(lastName: string): boolean {
  return lastName.trim().length >= 2;
}

/** Clearing first name should also wipe last name (they are generated together). */
export function shouldClearLastNameOnFirstNameEmpty(
  previousFirstName: string,
  nextFirstName: string,
): boolean {
  return previousFirstName !== '' && nextFirstName.trim() === '';
}

/** Grow the last-name input max so a pasted value is not truncated on next keystroke. */
export function nextMaxNomLength(currentMax: number, lastName: string): number {
  return Math.max(currentMax, lastName.trim().length + 1);
}

/** True when another student already uses this display name. */
export function isDisplayNameTaken(
  query: string,
  results: Array<{ id?: string; displayName?: string | null }> | undefined,
  excludeId?: string,
): boolean {
  if (!query || !results) return false;
  const needle = query.toLowerCase();
  return results.some(
    (student) =>
      student.displayName?.toLowerCase() === needle && (!excludeId || student.id !== excludeId),
  );
}

/** Levels whose cycle matches the establishment type; all levels when the type does not imply a cycle. */
export function filterLevelsForEstablishmentType<T extends { educationCycle?: string | null }>(
  levels: T[],
  establishmentType: string | null | undefined,
): T[] {
  const cycles = establishmentType ? ESTABLISHMENT_TYPE_TO_CYCLE[establishmentType] : undefined;
  if (!cycles?.length) return levels;
  return levels.filter((level) => !!level.educationCycle && cycles.includes(level.educationCycle));
}

/** `{ id, label }` catalog rows → select options. */
export function mapIdLabelOptions(
  items: Array<{ id: string; label: string }>,
): Array<{ value: string; label: string }> {
  return items.map((item) => ({ value: item.id, label: item.label }));
}

/** Specialties linked to the selected level (empty until a level is chosen). */
export function mapSpecialtyOptionsForLevel(
  specialties: Array<{ id: string; label: string; levelLinks?: Array<{ level: { id: string } }> }>,
  levelId: string,
): Array<{ value: string; label: string }> {
  if (!levelId) return [];
  return specialties
    .filter((specialty) => specialty.levelLinks?.some((link) => link.level.id === levelId))
    .map((specialty) => ({ value: specialty.id, label: specialty.label }));
}

/** Weekly start/end windows unioned from the selected programs. */
export function collectProgramAllowedSlots(
  programIds: string[] | undefined,
  programs: Array<{
    id: string;
    availability?: { weekly?: Array<{ start: string; end: string }> };
  }>,
): Array<{ start: string; end: string }> | undefined {
  if (!programIds?.length) return undefined;
  const selected = programs.filter((program) => programIds.includes(program.id));
  const slots = selected
    .flatMap((program) => program.availability?.weekly ?? [])
    .map((slot) => ({ start: slot.start, end: slot.end }));
  return slots.length > 0 ? slots : undefined;
}

/** `'registered'` / `'accompanied'` only; anything else → `null`. */
export function parseStudentReactivationTargetStatus(
  value: unknown,
): 'registered' | 'accompanied' | null {
  return value === 'registered' || value === 'accompanied' ? value : null;
}

/** Known type stays as-is; unknown free text is stored as `autre` + note. */
export function resolveSpecialNeeds(
  rawType?: string | null,
  rawNote?: string | null,
): { specialNeedsType: string; specialNeedsNote: string } {
  if (!rawType) return { specialNeedsType: '', specialNeedsNote: rawNote || '' };
  const lowerType = rawType.toLowerCase();
  if (KNOWN_SPECIAL_NEEDS_TYPES.has(lowerType)) {
    return { specialNeedsType: lowerType, specialNeedsNote: rawNote || '' };
  }
  return { specialNeedsType: 'autre', specialNeedsNote: rawType };
}

/** Map stored exit reason onto a known select value, or `other`. */
function resolveDeactivationReasonType(raw: string): string {
  if (EXIT_REASON_VALUES.includes(raw as (typeof EXIT_REASON_VALUES)[number])) return raw;
  if (raw) return 'other';
  return '';
}

/** Free-text note, or the raw reason when it was not a known enum value. */
function resolveDeactivationReasonOther(note: string | null | undefined, raw: string): string {
  if (note) return note;
  if (raw && !EXIT_REASON_VALUES.includes(raw as (typeof EXIT_REASON_VALUES)[number])) return raw;
  return '';
}

/** Profile exit date, then deactivation date; today when already deactivated. */
function resolveExitDate(
  profileExit: string | Date | null | undefined,
  deactivationDate: string | Date | null | undefined,
  status: string,
): string {
  const parsed = toCalendarDateString(profileExit ?? deactivationDate ?? null);
  if (parsed) return parsed;
  if (normalizeStudentAccountStatus(status) === 'deactivated') {
    return toLocalDateString(new Date());
  }
  return '';
}

/** Unwrap program objects to string ids. */
function normalizeProgramIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const record = item as { programId?: string; id?: string };
        return record.programId ?? record.id ?? '';
      }
      return '';
    })
    .filter((id) => id.length > 0);
}

/** Union of user-establishment ids and the school-class establishment. */
function collectEstablishmentIds(student: StudentRecord): string[] {
  const ueIds = (student.userEstablishments ?? [])
    .map((ue) => ue.establishmentId)
    .filter((id): id is string => !!id);
  const schoolClassEstId = student.userProfiles?.[0]?.schoolClass?.establishment?.id;
  return [...new Set([...ueIds, ...(schoolClassEstId ? [schoolClassEstId] : [])])];
}

/** Hydrate the edit form from a student record. */
export function studentToEditFormData(student: StudentRecord): EditStudentFormState {
  const profile = student.userProfiles?.[0];
  const rawReason = student.deactivationReason ?? '';
  return {
    firstName: student.firstName || '',
    lastName: (student.lastName || '').slice(0, 2),
    displayName: student.displayName || '',
    status: normalizeStudentAccountStatus(String(student.status)),
    dateOfBirth: profile?.dateOfBirth || '',
    gender: normalizeProfileGender(profile?.gender),
    levelId: profile?.levelId ?? '',
    schoolClassId: profile?.schoolClassId || '',
    addressLine1: profile?.addressLine1 || '',
    department: profile?.department || '',
    postalCode: profile?.postalCode || '',
    city: profile?.city || '',
    country: profile?.country || getDefaultCompanyCountryIso(),
    establishmentIds: collectEstablishmentIds(student),
    subjects: (profile?.subjects || []) as string[],
    availability: (profile?.availability as { weekly: WeeklyAvailabilitySlot[] }) || { weekly: [] },
    hasSpecialNeeds: profile?.hasSpecialNeeds || false,
    ...resolveSpecialNeeds(profile?.specialNeedsType, profile?.specialNeedsNote),
    isDisabled: profile?.isDisabled || false,
    disabilityTypes: profile?.disabilityTypes || [],
    disabilityOtherNote: profile?.disabilityOtherNote || '',
    medicalNotes: profile?.medicalNotes || '',
    specialtyIds: profile?.userProfileSpecialties?.map((ups) => ups.specialty.id) ?? [],
    deactivationReasonType: resolveDeactivationReasonType(rawReason),
    deactivationReasonOther: resolveDeactivationReasonOther(
      student.deactivationReasonNote,
      rawReason,
    ),
    exitDate: resolveExitDate(profile?.exitDate, student.deactivationDate, String(student.status)),
    programIds: normalizeProgramIds(student.programIds),
    coursePath: student.coursePath ?? '',
  };
}

/** Special-needs DTO fields; note only when type is `autre`. */
function specialNeedsDto(form: {
  hasSpecialNeeds: boolean;
  specialNeedsType: string;
  specialNeedsNote: string;
}) {
  return {
    hasSpecialNeeds: form.hasSpecialNeeds,
    specialNeedsType:
      form.hasSpecialNeeds && form.specialNeedsType ? form.specialNeedsType : undefined,
    specialNeedsNote:
      form.hasSpecialNeeds && form.specialNeedsType === 'autre' && form.specialNeedsNote
        ? form.specialNeedsNote
        : undefined,
  };
}

/** Disability DTO fields; other-note only when `autre` is selected. */
function disabilityDto(form: {
  isDisabled: boolean;
  disabilityTypes: string[];
  disabilityOtherNote: string;
}) {
  return {
    isDisabled: form.isDisabled,
    disabilityTypes:
      form.isDisabled && form.disabilityTypes.length > 0 ? form.disabilityTypes : undefined,
    disabilityOtherNote:
      form.isDisabled && form.disabilityTypes.includes('autre') && form.disabilityOtherNote
        ? form.disabilityOtherNote
        : undefined,
  };
}

/** Build `POST` student DTO from the create form. */
export function buildCreateStudentDto(
  form: CreateStudentFormState,
  studentRoleId: string,
): CreateStudentDto {
  const needs = specialNeedsDto(form);
  const disability = disabilityDto(form);
  return {
    firstName: form.firstName,
    lastName: form.lastName,
    displayName: form.displayName || undefined,
    dateOfBirth: form.dateOfBirth || undefined,
    gender: form.gender || undefined,
    levelId: form.levelId || undefined,
    schoolClassId: form.schoolClassId || undefined,
    addressLine1: form.addressLine1 || undefined,
    department: form.department || undefined,
    postalCode: form.postalCode || undefined,
    city: form.city || undefined,
    country: getDefaultCompanyCountryIso(),
    medicalNotes: form.medicalNotes || undefined,
    roleIds: [studentRoleId],
    establishmentIds: form.establishmentIds,
    subjects: form.subjects.length > 0 ? form.subjects : undefined,
    availability: form.availability.weekly.length > 0 ? form.availability : undefined,
    ...needs,
    ...disability,
    specialtyIds: form.specialtyIds.length > 0 ? form.specialtyIds : undefined,
    programIds: form.programIds.length > 0 ? form.programIds : undefined,
    coursePath: form.coursePath || undefined,
  };
}

/** Build `PATCH` student DTO from the edit form. */
export function buildUpdateStudentDto(form: EditStudentFormState): UpdateStudentDto {
  const needs = specialNeedsDto(form);
  const isDeactivated = form.status === 'deactivated';
  return {
    firstName: form.firstName,
    lastName: form.lastName,
    displayName: form.displayName || undefined,
    status: form.status as StudentAccountStatus,
    dateOfBirth: form.dateOfBirth || undefined,
    gender: form.gender as 'male' | 'female' | 'other' | undefined,
    levelId: form.levelId || undefined,
    schoolClassId: form.schoolClassId || null,
    addressLine1: form.addressLine1 || undefined,
    department: form.department || undefined,
    postalCode: form.postalCode || undefined,
    city: form.city || undefined,
    country: getDefaultCompanyCountryIso(),
    establishmentIds: form.establishmentIds.length > 0 ? form.establishmentIds : undefined,
    subjects: form.subjects.length > 0 ? form.subjects : undefined,
    availability: form.availability.weekly.length > 0 ? form.availability : undefined,
    ...needs,
    isDisabled: form.isDisabled,
    disabilityTypes: form.isDisabled && form.disabilityTypes.length > 0 ? form.disabilityTypes : [],
    disabilityOtherNote:
      form.isDisabled && form.disabilityTypes.includes('autre') && form.disabilityOtherNote
        ? form.disabilityOtherNote
        : undefined,
    medicalNotes: form.medicalNotes || undefined,
    specialtyIds: form.specialtyIds.length > 0 ? form.specialtyIds : undefined,
    programIds: form.programIds.length > 0 ? form.programIds : undefined,
    coursePath: form.coursePath || undefined,
    deactivationReason: isDeactivated ? form.deactivationReasonType || undefined : undefined,
    deactivationReasonNote:
      isDeactivated && form.deactivationReasonType === 'other'
        ? form.deactivationReasonOther || undefined
        : undefined,
    exitDate: isDeactivated ? form.exitDate || undefined : undefined,
  };
}

/** Whether create submit is blocked by required fields / uniqueness. */
export function isCreateStudentFormValid(
  form: CreateStudentFormState,
  displayNameTaken: boolean,
): boolean {
  return (
    form.firstName.trim().length >= 2 &&
    form.lastName.trim().length >= 2 &&
    form.gender !== '' &&
    form.establishmentIds.length > 0 &&
    form.levelId.trim().length > 0 &&
    !displayNameTaken
  );
}

/** Whether edit submit is blocked by required fields / uniqueness / exit date. */
export function isEditStudentFormValid(
  form: EditStudentFormState,
  displayNameTaken: boolean,
): boolean {
  return (
    form.firstName.trim().length >= 2 &&
    form.lastName.trim().length >= 2 &&
    form.gender !== '' &&
    form.establishmentIds.length > 0 &&
    form.levelId.trim().length > 0 &&
    (form.status !== 'deactivated' || form.exitDate.trim().length > 0) &&
    !displayNameTaken
  );
}

/** Role id for `code === 'student'`. */
export function findStudentRoleId(roles: Array<{ id: string; code: string }>): string | undefined {
  return roles.find((role) => role.code === 'student')?.id;
}

/** Success / skip / error toasts from an import result. */
export function resolveStudentImportToasts(result: ImportCsvResult): StudentImportToast[] {
  const toasts: StudentImportToast[] = [];
  if (result.importedCount > 0) {
    const skipped =
      result.skippedExistingEmailCount > 0
        ? `, ${result.skippedExistingEmailCount} doublons ignorés`
        : '';
    toasts.push({
      kind: 'success',
      message: `Import réussi ! ${result.importedCount} élève(s) importé(s)${skipped}`,
    });
  } else if (result.skippedExistingEmailCount > 0) {
    toasts.push({
      kind: 'info',
      message: `Aucun nouvel élève importé. ${result.skippedExistingEmailCount} doublons ignorés.`,
    });
  } else {
    toasts.push({ kind: 'warning', message: getErrorMessage('studentImportNone') });
  }
  if (result.errors.length > 0) {
    toasts.push({
      kind: 'error',
      message: getErrorMessage({
        code: 'studentImportInvalidRows',
        invalidCount: result.invalidCount,
      }),
    });
  }
  return toasts;
}

/** Same in-person departments as the tutor eligible picker (student form has no department field). */
export const IN_PERSON_STUDENT_ELIGIBLE_DEPARTMENTS: string[] = [
  ...ALLOWED_INPERSON_TUTOR_DEPARTMENT_CODES,
];

/** Eligible establishment row from `GET .../establishments/eligible`. */
export type EligibleEstablishmentOptionSource = {
  id?: string;
  name?: string;
  city?: string | null;
  postalCode?: string | null;
  department?: string | null;
  departmentCode?: string | null;
  country?: string | null;
};

/** Map eligible establishments to autocomplete `{ value, label }`. */
export function mapEligibleEstablishmentOptions(
  establishments: EligibleEstablishmentOptionSource[],
): SelectOption[] {
  const options: SelectOption[] = [];
  for (const establishment of establishments) {
    if (!establishment.id || !establishment.name) continue;
    options.push({
      value: establishment.id,
      label: formatEstablishmentSelectLabel({
        name: establishment.name,
        city: establishment.city,
        postalCode: establishment.postalCode,
        department: establishment.departmentCode ?? establishment.department,
        country: establishment.country,
      }),
    });
  }
  return options;
}

/** First selected establishment id, or empty string. */
export function firstEstablishmentId(establishmentIds: string[]): string {
  return establishmentIds[0] ?? '';
}

/** Keep the currently selected establishment in the options even if filters hide it. */
export function appendSelectedEstablishmentOption(
  options: SelectOption[],
  selectedId: string,
  labelById: Map<string, string>,
): SelectOption[] {
  if (!selectedId) return options;
  if (options.some((opt) => opt.value === selectedId)) return options;
  return [{ value: selectedId, label: labelById.get(selectedId) ?? selectedId }, ...options];
}

/** Merge formatted establishment labels into the cache; reuse `prev` when unchanged. */
export function mergeEstablishmentLabelCache(
  prev: Map<string, string>,
  entries: EstablishmentLabelEntry[],
): Map<string, string> {
  const next = new Map(prev);
  let changed = false;
  for (const entry of entries) {
    if (!entry.id || !entry.name?.trim()) continue;
    const label = formatEstablishmentSelectLabel({ ...entry, name: entry.name });
    if (next.get(entry.id) !== label) {
      next.set(entry.id, label);
      changed = true;
    }
  }
  return changed ? next : prev;
}

/** True when a coordinator lock should overwrite the current establishment ids. */
export function shouldApplySingleEstablishmentLock(
  isSingleLocked: boolean,
  singleId: string | undefined,
  establishmentIds: string[],
): boolean {
  if (!isSingleLocked || !singleId) return false;
  return !(establishmentIds.length === 1 && establishmentIds[0] === singleId);
}
